import { DOCUMENT, DestroyRef, Injectable, Injector, afterNextRender, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { flowStepOf } from './flow-transition';

/** Hasta dónde puede arrancar el contenido, como fracción de la pantalla, sin que haga falta mover la página. */
const MAX_CONTENT_START = 0.6;

/**
 * Al cambiar de paso, el cliente tiene que ver el contenido del paso nuevo. En una laptop el header y el hero ocupan casi
 * toda la pantalla, y al salir de documentos la página suele estar muy abajo: en esos casos la lleva al inicio del
 * contenido. Si el contenido ya se ve, no toca nada y el marco de la página queda quieto.
 *
 * Para ajustar dónde queda: `scroll-margin-top` de `.page-content`.
 *
 * Después del render porque hasta entonces no está el hero, y antes de la foto de la página nueva: la transición del
 * router recién la pide un `setTimeout` más tarde.
 */
@Injectable({ providedIn: 'root' })
export class FlowScroll {
  private readonly document = inject(DOCUMENT);
  private readonly injector = inject(Injector);
  private readonly router = inject(Router);
  private lastStep: number | null = null;

  constructor() {
    const subscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.onNavigationEnd();
      }
    });
    inject(DestroyRef).onDestroy(() => subscription.unsubscribe());
  }

  private onNavigationEnd(): void {
    const step = flowStepOf(this.router.routerState.snapshot.root);
    const changedStep = this.lastStep !== null && step !== null && step !== this.lastStep;
    this.lastStep = step;
    if (changedStep) {
      afterNextRender(() => this.revealContent(), { injector: this.injector });
    }
  }

  private revealContent(): void {
    const content = this.document.querySelector('.page-content');
    if (!content) {
      return;
    }
    const { top } = content.getBoundingClientRect();
    const maxTop = this.document.documentElement.clientHeight * MAX_CONTENT_START;
    if (top < 0 || top > maxTop) {
      // `instant`: `auto` hereda el `scroll-behavior: smooth` de html y seguiría animándose al sacar la foto.
      content.scrollIntoView({ block: 'start', behavior: 'instant' });
    }
  }
}
